import React, { useState, useMemo } from 'react';
import { ColumnDef, ColumnFiltersState } from '@tanstack/react-table';
import { TradeOrder } from '../api/tradeGroups';
import DataTable, { DataTableFilter } from './shared/DataTable';
import './OrdersTable.css';

interface OrdersTableProps {
  orders: TradeOrder[];
  currentPrice: number;
}

const OrdersTable: React.FC<OrdersTableProps> = ({ orders, currentPrice }) => {
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState('');

  const formatCurrency = (value: number | null | undefined) => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 8,
    }).format(value);
  };

  const formatDateTime = (timestamp: number | null) => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short',
    });
  };

  const calculatePnL = (order: TradeOrder) => {
    // Only show unrealized P&L for FILLED BUY orders that DON'T have a completed sell
    // If the sell is FILLED, the profit is realized - show it in the Realized Profit column instead
    if (order.side === 'BUY' && order.status === 'FILLED' && order.averageFilledPrice) {
      // Don't show unrealized if sell is already filled (profit is now realized)
      if (order.sellTargetStatus === 'FILLED') {
        return null;
      }
      return (currentPrice - order.averageFilledPrice) * (order.baseSize);
    }
    return null;
  };

  const calculateRealizedProfit = (order: TradeOrder) => {
    // Show realized profit on BUY orders that have a FILLED sell
    if (order.side === 'BUY' && order.sellTargetStatus === 'FILLED' && order.averageFilledPrice && order.sellTargetPrice) {
      const buyPrice = order.averageFilledPrice;
      const sellPrice = order.sellTargetPrice;
      const size = order.baseSize;
      return (sellPrice - buyPrice) * size;
    }
    return null;
  };

  const columns = useMemo<ColumnDef<TradeOrder>[]>(
    () => [
      {
        accessorKey: 'createdAt',
        header: 'Created',
        cell: (info) => <span className="date-cell">{formatDateTime(info.getValue() as number)}</span>,
        sortingFn: 'basic',
      },
      {
        accessorKey: 'lastModified',
        header: 'Last Modified',
        cell: (info) => <span className="date-cell">{formatDateTime(info.getValue() as number)}</span>,
        sortingFn: 'basic',
      },
      {
        accessorKey: 'side',
        header: 'Side',
        cell: (info) => (
          <span className={`badge badge-${(info.getValue() as string).toLowerCase()}`}>
            {info.getValue() as string}
          </span>
        ),
        filterFn: 'equals',
      },
      {
        accessorKey: 'orderType',
        header: 'Order Type',
        cell: (info) => (
          <span className={`badge badge-${(info.getValue() as string).toLowerCase()}`}>
            {info.getValue() as string}
          </span>
        ),
        filterFn: 'equals',
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: (info) => (
          <span className={`badge badge-status badge-${(info.getValue() as string).toLowerCase()}`}>
            {info.getValue() as string}
          </span>
        ),
        filterFn: 'equals',
      },
      {
        accessorKey: 'price',
        header: 'Price',
        cell: (info) => {
          const order = info.row.original;
          const price = info.getValue() as number;
          const isPriceAboveCurrent = price > currentPrice;
          const isPriceBelowCurrent = price < currentPrice;
          return (
            <span className="price-cell">
              {formatCurrency(price)}
              {order.side === 'BUY' && isPriceBelowCurrent && <span className="price-indicator below">↓</span>}
              {order.side === 'BUY' && isPriceAboveCurrent && <span className="price-indicator above">↑</span>}
            </span>
          );
        },
        sortingFn: 'basic',
      },
      {
        accessorKey: 'baseSize',
        header: 'Size',
        cell: (info) => (info.getValue() as number)?.toFixed(6) || '-',
        sortingFn: 'basic',
      },
      {
        id: 'orderAmount',
        header: 'Order Amount',
        accessorFn: (row) => {
          if (row.quoteSize && row.quoteSize > 0) return row.quoteSize;
          if (row.limitPrice && row.limitPrice > 0) return row.limitPrice * row.baseSize;
          if (row.averageFilledPrice && row.averageFilledPrice > 0) return row.averageFilledPrice * row.baseSize;
          if (row.expectedEntryPrice && row.expectedEntryPrice > 0) return row.expectedEntryPrice * row.baseSize;
          return 0;
        },
        cell: (info) => formatCurrency(info.getValue() as number),
        sortingFn: 'basic',
      },
      {
        id: 'unrealizedPnL',
        header: 'Unrealized P&L',
        accessorFn: (row) => calculatePnL(row),
        cell: (info) => {
          const pnl = info.getValue() as number | null;
          return (
            <span className={pnl !== null ? (pnl >= 0 ? 'pnl-positive' : 'pnl-negative') : ''}>
              {pnl !== null ? formatCurrency(pnl) : '-'}
            </span>
          );
        },
        sortingFn: 'basic',
      },
      {
        id: 'realizedProfit',
        header: 'Realized Profit',
        accessorFn: (row) => calculateRealizedProfit(row),
        cell: (info) => {
          const profit = info.getValue() as number | null;
          return (
            <span className={profit !== null ? (profit >= 0 ? 'pnl-positive' : 'pnl-negative') : ''}>
              {profit !== null ? formatCurrency(profit) : '-'}
            </span>
          );
        },
        sortingFn: 'basic',
      },
      {
        accessorKey: 'sellTargetPrice',
        header: 'Sell Target',
        cell: (info) => {
          const order = info.row.original;
          const price = info.getValue() as number;
          return price ? (
            <>
              {formatCurrency(price)}
              {order.sellTargetStatus && (
                <span className={`sell-status ${order.sellTargetStatus.toLowerCase()}`}>
                  ({order.sellTargetStatus})
                </span>
              )}
            </>
          ) : '-';
        },
        sortingFn: 'basic',
      },
      {
        accessorKey: 'totalFees',
        header: 'Fees',
        cell: (info) => <span className="fee-cell">{formatCurrency(info.getValue() as number)}</span>,
        sortingFn: 'basic',
      },
      {
        accessorKey: 'creationTrigger',
        header: 'Creation Trigger',
        cell: (info) => {
          const trigger = info.getValue() as string | null;
          if (!trigger) return <span className="trigger-cell empty">-</span>;
          const formatted = trigger
            .split('_')
            .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
            .join(' ');
          return (
            <span className="trigger-cell" title={trigger}>
              {formatted}
            </span>
          );
        },
        sortingFn: 'basic',
        filterFn: 'equals',
      },
      {
        accessorKey: 'cancellationTrigger',
        header: 'Cancellation Trigger',
        cell: (info) => {
          const trigger = info.getValue() as string | null;
          if (!trigger) return <span className="trigger-cell empty">-</span>;
          const formatted = trigger
            .split('_')
            .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
            .join(' ');
          return (
            <span className="trigger-cell cancellation" title={trigger}>
              {formatted}
            </span>
          );
        },
        sortingFn: 'basic',
        filterFn: 'equals',
      },
      {
        accessorKey: 'orderId',
        header: 'Order ID',
        cell: (info) => {
          const orderId = info.getValue() as string;
          return orderId ? (
            <span className="order-id-cell" title={orderId}>{orderId.substring(0, 8)}...</span>
          ) : (
            <span className="order-id-cell no-order-id">Not submitted</span>
          );
        },
        enableSorting: false,
      },
    ],
    [currentPrice]
  );

  const uniqueValues = useMemo(() => {
    return {
      sides: Array.from(new Set(orders.map((o) => o.side))),
      statuses: Array.from(new Set(orders.map((o) => o.status))),
      creationTriggers: Array.from(new Set(orders.map((o) => o.creationTrigger).filter((t): t is string => t !== null))),
      cancellationTriggers: Array.from(new Set(orders.map((o) => o.cancellationTrigger).filter((t): t is string => t !== null))),
    };
  }, [orders]);

  const formatTrigger = (t: string) =>
    t.split('_').map((w) => w.charAt(0) + w.slice(1).toLowerCase()).join(' ');

  const setColumnFilter = (id: string, value: string) => {
    setColumnFilters((prev) =>
      value === '' ? prev.filter((f) => f.id !== id) : [...prev.filter((f) => f.id !== id), { id, value }]
    );
  };
  const getColumnFilterValue = (id: string): string =>
    (columnFilters.find((f) => f.id === id)?.value as string | undefined) ?? '';

  const filters: DataTableFilter[] = [
    {
      id: 'side',
      label: 'Side',
      value: getColumnFilterValue('side'),
      options: uniqueValues.sides.map((s) => ({ value: s, label: s })),
      onChange: (v) => setColumnFilter('side', v),
    },
    {
      id: 'status',
      label: 'Status',
      value: getColumnFilterValue('status'),
      options: uniqueValues.statuses.map((s) => ({ value: s, label: s })),
      onChange: (v) => setColumnFilter('status', v),
    },
    {
      id: 'creationTrigger',
      label: 'Creation',
      value: getColumnFilterValue('creationTrigger'),
      options: uniqueValues.creationTriggers.map((t) => ({ value: t, label: formatTrigger(t) })),
      onChange: (v) => setColumnFilter('creationTrigger', v),
    },
    {
      id: 'cancellationTrigger',
      label: 'Cancellation',
      value: getColumnFilterValue('cancellationTrigger'),
      options: uniqueValues.cancellationTriggers.map((t) => ({ value: t, label: formatTrigger(t) })),
      onChange: (v) => setColumnFilter('cancellationTrigger', v),
    },
  ];

  // After filters apply, count is what TanStack reports — but DataTable owns that.
  // We compute a quick estimate for the filter-info text via the same predicates.
  const visibleCount = useMemo(() => {
    let result = orders;
    for (const f of columnFilters) {
      result = result.filter((o) => (o as unknown as Record<string, unknown>)[f.id] === f.value);
    }
    if (globalFilter) {
      const needle = globalFilter.toLowerCase();
      result = result.filter((o) =>
        Object.values(o as unknown as Record<string, unknown>)
          .filter((v) => v != null)
          .some((v) => String(v).toLowerCase().includes(needle))
      );
    }
    return result.length;
  }, [orders, columnFilters, globalFilter]);

  return (
    <DataTable<TradeOrder>
      data={orders}
      columns={columns}
      defaultSorting={[{ id: 'createdAt', desc: true }]}
      filters={filters}
      columnFilters={columnFilters}
      onColumnFiltersChange={setColumnFilters}
      search={{ placeholder: 'Search all columns…', value: globalFilter, onChange: setGlobalFilter }}
      filterInfo={`Showing ${visibleCount} of ${orders.length} orders`}
      emptyMessage="No orders match the selected filters."
      ariaLabel="Orders"
      rowClassName={(order) => `order-row ${order.side.toLowerCase()}`}
    />
  );
};

export default OrdersTable;
